import { Info, Printer } from "lucide-react";

import {
  isAutoPrint,
  isPrintType,
} from "~/components/stations/station-edit-types";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { parseEnumValue } from "~/lib/parse-enum-value";

import type { StationColorType } from "./station-wizard-types";

export const StepPrinting = ({
  name,
  selectedPrinterIds,
  setSelectedPrinterIds,
  autoPrint,
  setAutoPrint,
  printType,
  setPrintType,
  printOrderTypes,
  setPrintOrderTypes,
  printersAvailable,
  color,
}: {
  name: string;
  selectedPrinterIds: string[];
  setSelectedPrinterIds: (ids: string[]) => void;
  autoPrint: "off" | "onReceived" | "onBump";
  setAutoPrint: (mode: "off" | "onReceived" | "onBump") => void;
  printType: "ticket" | "label";
  setPrintType: (type: "ticket" | "label") => void;
  printOrderTypes: string[];
  setPrintOrderTypes: (types: string[]) => void;
  printersAvailable: { value: string; label: string }[];
  color: StationColorType;
}) => (
  <div className="flex flex-col gap-5">
    <div className="flex flex-col items-center">
      <h1 className="h4">Printing Settings</h1>
      <p className="body text-muted-foreground">
        Configure kitchen ticket printing (optional)
      </p>
    </div>

    <div className="flex flex-col items-center">
      <div
        className="flex h-12 w-40 items-center justify-center rounded-xl text-center font-medium"
        style={{ background: color.color, stroke: color.stroke }}
      >
        {name}
      </div>
    </div>

    <div className="bg-info/10 text-info flex gap-2 rounded-xl px-4 py-3">
      <Info className="h-5 w-5 shrink-0" />
      <p className="text-sm">
        You can skip this step if you don't need printing. Printers must be set
        up on your POS first.
      </p>
    </div>

    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p className="body font-medium">
          Printers{" "}
          {selectedPrinterIds.length > 0 && (
            <span className="text-muted-foreground">
              ({selectedPrinterIds.length} selected)
            </span>
          )}
        </p>
        {printersAvailable.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No printers configured. Add printers in Settings → Printers.
          </p>
        ) : (
          <div className="border-border space-y-2 rounded-lg border p-3">
            {printersAvailable.map((printer) => {
              const isSelected = selectedPrinterIds.includes(printer.value);
              const checkboxId = `new-printer-${printer.value}`;
              return (
                <label
                  className="hover:bg-accent flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5"
                  htmlFor={checkboxId}
                  key={printer.value}
                >
                  <Checkbox
                    checked={isSelected}
                    id={checkboxId}
                    onCheckedChange={(checked: boolean) => {
                      if (checked) {
                        setSelectedPrinterIds([
                          ...selectedPrinterIds,
                          printer.value,
                        ]);
                      } else {
                        setSelectedPrinterIds(
                          selectedPrinterIds.filter(
                            (id) => id !== printer.value
                          )
                        );
                      }
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <Printer className="text-muted-foreground h-4 w-4" />
                    <span>{printer.label}</span>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {selectedPrinterIds.length > 0 && (
        <>
          <div className="flex flex-col gap-2">
            <p className="body font-medium">Auto-Print</p>
            <Select
              onValueChange={(v) =>
                setAutoPrint(parseEnumValue(v, isAutoPrint, "off"))
              }
              value={autoPrint}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off - Manual printing only</SelectItem>
                <SelectItem value="onReceived">
                  On Received - Print when order arrives
                </SelectItem>
                <SelectItem value="onBump">
                  On Bump - Print when order is bumped on KDS
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <p className="body font-medium">Print Format</p>
            <Select
              onValueChange={(v) =>
                setPrintType(parseEnumValue(v, isPrintType, "ticket"))
              }
              value={printType}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ticket">
                  Ticket - Standard kitchen ticket
                </SelectItem>
                <SelectItem value="label">
                  Label - Compact label format
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <p className="body font-medium">Print for Order Types</p>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "dineIn", label: "Dine In" },
                { value: "takeout", label: "Takeout" },
                { value: "delivery", label: "Delivery" },
              ].map((orderType) => {
                const checkboxId = `new-order-type-${orderType.value}`;
                return (
                  <label
                    className="border-border hover:bg-accent flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2"
                    htmlFor={checkboxId}
                    key={orderType.value}
                  >
                    <Checkbox
                      checked={printOrderTypes.includes(orderType.value)}
                      id={checkboxId}
                      onCheckedChange={(checked: boolean) => {
                        if (checked) {
                          setPrintOrderTypes([
                            ...printOrderTypes,
                            orderType.value,
                          ]);
                        } else {
                          setPrintOrderTypes(
                            printOrderTypes.filter((t) => t !== orderType.value)
                          );
                        }
                      }}
                    />
                    <span className="text-sm">{orderType.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  </div>
);

export const StepReview = ({
  name,
  hasScreen,
  routingMode,
  categoriesSelected,
  itemsSelected,
  selectedPrinterIds,
  autoPrint,
  printersAvailable,
  color,
}: {
  name: string;
  hasScreen: boolean;
  routingMode: "all" | "categories" | "items";
  categoriesSelected: string[];
  itemsSelected: string[];
  selectedPrinterIds: string[];
  autoPrint: "off" | "onReceived" | "onBump";
  printersAvailable: { value: string; label: string }[];
  color: StationColorType;
}) => {
  const printerNames =
    selectedPrinterIds.length > 0
      ? selectedPrinterIds
          .map((id) => printersAvailable.find((p) => p.value === id)?.label)
          .filter(Boolean)
          .join(", ")
      : "None";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col items-center">
        <h1 className="h4">Review Station</h1>
        <p className="body text-muted-foreground">
          Confirm your station settings
        </p>
      </div>

      <div className="flex flex-col items-center">
        <div
          className="flex h-20 w-52 flex-col items-center justify-center rounded-xl text-center"
          style={{ background: color.color, stroke: color.stroke }}
        >
          <p className="font-medium">{name}</p>
          <div className="text-muted-foreground mt-1 flex gap-2 text-xs">
            {hasScreen && <span>Screen</span>}
            {selectedPrinterIds.length > 0 && <span>Printer</span>}
          </div>
        </div>
      </div>

      <div className="bg-accent space-y-3 rounded-xl p-4">
        <div className="flex justify-between">
          <span className="text-muted-foreground">KDS Screen</span>
          <span className="font-medium">{hasScreen ? "Yes" : "No"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Routing</span>
          <span className="font-medium">
            {(() => {
              if (routingMode === "all") {
                return "All Items";
              }
              if (routingMode === "categories") {
                return `${categoriesSelected.length} Categories`;
              }
              return `${itemsSelected.length} Items`;
            })()}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Printers</span>
          <span className="font-medium">{printerNames}</span>
        </div>
        {selectedPrinterIds.length > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Auto-Print</span>
            <span className="font-medium">
              {(() => {
                if (autoPrint === "off") {
                  return "Off";
                }
                if (autoPrint === "onReceived") {
                  return "On Received";
                }
                return "On Bump";
              })()}
            </span>
          </div>
        )}
      </div>

      {hasScreen && (
        <div className="bg-muted/50 flex gap-2 rounded-xl px-4 py-3">
          <Info className="text-muted-foreground h-5 w-5 shrink-0" />
          <p className="text-muted-foreground text-sm">
            Additional display settings (fonts, sounds, timing, cross-screen
            modes) can be configured after creation by editing the station.
          </p>
        </div>
      )}
    </div>
  );
};
